/**
 * Microphone recording helper with two interchangeable engines behind one API:
 *
 *  - MediaRecorder  — modern browsers (Chrome/Firefox/Edge, iOS Safari >= 14.5).
 *                     Produces a webm/opus or mp4/aac blob.
 *  - ScriptProcessor -> WAV — fallback for browsers that expose getUserMedia and
 *                     the Web Audio API but no MediaRecorder (iOS Safari 11-14.4,
 *                     very old Android Chrome). Produces a 16 kHz mono WAV blob,
 *                     which stays comfortably under the backend upload limit for
 *                     a 60 second recording.
 *
 * The backend transcodes whatever we send to a normalized MP3, so the exact
 * container/codec here does not matter as long as ffmpeg can read it.
 *
 * Written defensively for old mobile engines: no reliance on polyfilled
 * instance methods (Array.prototype.includes, String.prototype.padStart, ...),
 * prefixed getUserMedia / AudioContext are supported, and every resource is
 * released by dispose() so the browser microphone indicator always clears.
 */

var MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus'
]

var WAV_SAMPLE_RATE = 16000

function getNavigator () {
  return (typeof window !== 'undefined' && window.navigator) || null
}

function hasGetUserMedia () {
  var nav = getNavigator()
  if (!nav) { return false }
  if (nav.mediaDevices && nav.mediaDevices.getUserMedia) { return true }
  return !!(nav.getUserMedia || nav.webkitGetUserMedia || nav.mozGetUserMedia || nav.msGetUserMedia)
}

function getAudioContextClass () {
  if (typeof window === 'undefined') { return null }
  return window.AudioContext || window.webkitAudioContext || null
}

/**
 * Returns the mime type to record with, or null when MediaRecorder cannot be
 * used and the WAV fallback should take over.
 *   - null       -> use the WAV engine
 *   - ''         -> use MediaRecorder with its default mime type
 *   - '<string>' -> use MediaRecorder with that mime type
 */
function pickMimeType () {
  if (typeof window !== 'undefined' && window.__forceWavRecorder) { return null }
  if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') { return null }
  var MR = window.MediaRecorder
  if (typeof MR.isTypeSupported !== 'function') {
    // Old MediaRecorder without capability query: try the default and hope.
    return ''
  }
  for (var i = 0; i < MIME_CANDIDATES.length; i++) {
    if (MR.isTypeSupported(MIME_CANDIDATES[i])) { return MIME_CANDIDATES[i] }
  }
  return null
}

function isInsecureContext () {
  return typeof window !== 'undefined' && window.isSecureContext === false
}

export function isRecordingSupported () {
  // In an insecure context (plain HTTP on a LAN IP or any hostname other than
  // localhost) browsers hide navigator.mediaDevices entirely, so getUserMedia
  // cannot be feature-detected. Fall back to the recording APIs that ARE still
  // exposed there, so the entry point stays visible; startRecording() then
  // surfaces a clear "needs a secure connection" message instead of the link
  // silently disappearing.
  if (isInsecureContext()) {
    return (typeof window.MediaRecorder !== 'undefined') || getAudioContextClass() !== null
  }
  return hasGetUserMedia() && (pickMimeType() !== null || getAudioContextClass() !== null)
}

function requestStream () {
  var nav = getNavigator()
  if (nav && nav.mediaDevices && nav.mediaDevices.getUserMedia) {
    return nav.mediaDevices.getUserMedia({ audio: true })
  }
  var legacy = nav && (nav.getUserMedia || nav.webkitGetUserMedia || nav.mozGetUserMedia || nav.msGetUserMedia)
  if (legacy) {
    return new Promise(function (resolve, reject) {
      legacy.call(nav, { audio: true }, resolve, reject)
    })
  }
  return Promise.reject(new Error('getUserMedia not supported'))
}

function mergeBuffers (buffers, totalLength) {
  var result = new Float32Array(totalLength)
  var offset = 0
  for (var i = 0; i < buffers.length; i++) {
    result.set(buffers[i], offset)
    offset += buffers[i].length
  }
  return result
}

function downsample (buffer, fromRate, toRate) {
  if (toRate >= fromRate) { return buffer }
  var ratio = fromRate / toRate
  var newLength = Math.round(buffer.length / ratio)
  var result = new Float32Array(newLength)
  var offsetResult = 0
  var offsetBuffer = 0
  while (offsetResult < newLength) {
    var nextOffsetBuffer = Math.round((offsetResult + 1) * ratio)
    var accum = 0
    var count = 0
    for (var i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
      accum += buffer[i]
      count++
    }
    result[offsetResult] = count > 0 ? accum / count : 0
    offsetResult++
    offsetBuffer = nextOffsetBuffer
  }
  return result
}

function writeString (view, offset, str) {
  for (var i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i))
  }
}

function encodeWavBlob (samples, sampleRate) {
  var bytesPerSample = 2
  var blockAlign = bytesPerSample // mono
  var dataSize = samples.length * bytesPerSample
  var buffer = new ArrayBuffer(44 + dataSize)
  var view = new DataView(buffer)

  writeString(view, 0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true)
  writeString(view, 8, 'WAVE')
  writeString(view, 12, 'fmt ')
  view.setUint32(16, 16, true) // PCM subchunk size
  view.setUint16(20, 1, true) // audio format = PCM
  view.setUint16(22, 1, true) // channels = mono
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * blockAlign, true) // byte rate
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, 8 * bytesPerSample, true) // bits per sample
  writeString(view, 36, 'data')
  view.setUint32(40, dataSize, true)

  var offset = 44
  for (var i = 0; i < samples.length; i++) {
    var s = Math.max(-1, Math.min(1, samples[i]))
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true)
    offset += 2
  }

  return new Blob([buffer], { type: 'audio/wav' })
}

/**
 * Create a recorder instance. Usage:
 *   var rec = createRecorder()
 *   rec.start().then(...).catch(handlePermissionError)
 *   rec.stop().then(function (result) { result.blob, result.mimeType, result.duration })
 *   rec.dispose()
 */
export function createRecorder () {
  var stream = null
  var engine = null
  var disposed = false
  var startedAt = 0

  // MediaRecorder engine state
  var mediaRecorder = null
  var chunks = []
  var recordedMime = ''

  // WAV engine state
  var audioCtx = null
  var sourceNode = null
  var processorNode = null
  var gainNode = null
  var wavBuffers = []
  var wavLength = 0
  var wavSampleRate = 0

  function now () {
    return (typeof Date !== 'undefined' && Date.now) ? Date.now() : new Date().getTime()
  }

  function stopTracks () {
    if (stream) {
      var tracks = stream.getTracks ? stream.getTracks() : []
      for (var i = 0; i < tracks.length; i++) {
        try { tracks[i].stop() } catch (e) {}
      }
      stream = null
    }
  }

  function teardownWavNodes () {
    if (processorNode) {
      processorNode.onaudioprocess = null
      try { processorNode.disconnect() } catch (e) {}
    }
    if (sourceNode) { try { sourceNode.disconnect() } catch (e) {} }
    if (gainNode) { try { gainNode.disconnect() } catch (e) {} }
    processorNode = null
    sourceNode = null
    gainNode = null
  }

  function closeAudioCtx () {
    if (audioCtx && audioCtx.state !== 'closed' && audioCtx.close) {
      try { audioCtx.close() } catch (e) {}
    }
    audioCtx = null
  }

  function setupMediaRecorder (mime) {
    var MR = window.MediaRecorder
    try {
      mediaRecorder = mime ? new MR(stream, { mimeType: mime }) : new MR(stream)
    } catch (e) {
      mediaRecorder = new MR(stream)
    }
    recordedMime = mediaRecorder.mimeType || mime || ''
    chunks = []
    mediaRecorder.ondataavailable = function (e) {
      if (e.data && e.data.size > 0) { chunks.push(e.data) }
    }
    // No timeslice: a single blob at stop() avoids fragment quirks on old Safari.
    mediaRecorder.start()
  }

  function setupWavNodes () {
    sourceNode = audioCtx.createMediaStreamSource(stream)
    processorNode = audioCtx.createScriptProcessor(4096, 1, 1)
    gainNode = audioCtx.createGain()
    gainNode.gain.value = 0 // silence: the processor must reach a destination to run
    wavBuffers = []
    wavLength = 0
    processorNode.onaudioprocess = function (e) {
      var input = e.inputBuffer.getChannelData(0)
      // getChannelData returns a reused buffer; copy it.
      wavBuffers.push(new Float32Array(input))
      wavLength += input.length
    }
    sourceNode.connect(processorNode)
    processorNode.connect(gainNode)
    gainNode.connect(audioCtx.destination)
  }

  function finalBlobType () {
    if (mediaRecorder && mediaRecorder.mimeType) { return mediaRecorder.mimeType }
    if (recordedMime) { return recordedMime }
    if (chunks.length && chunks[0].type) { return chunks[0].type }
    return 'audio/webm'
  }

  function stopMediaRecorder () {
    return new Promise(function (resolve) {
      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        resolve(new Blob(chunks, { type: finalBlobType() }))
        return
      }
      mediaRecorder.onstop = function () {
        resolve(new Blob(chunks, { type: finalBlobType() }))
      }
      try {
        mediaRecorder.stop()
      } catch (e) {
        resolve(new Blob(chunks, { type: finalBlobType() }))
      }
    })
  }

  function start () {
    if (disposed) { return Promise.reject(new Error('recorder disposed')) }
    // getUserMedia only works in a secure context. Some browsers still expose a
    // non-functional legacy navigator.getUserMedia alias over plain HTTP, so
    // trust the secure-context signal rather than API presence here.
    if (isInsecureContext()) {
      var insecure = new Error('insecure context')
      insecure.name = 'InsecureContextError'
      return Promise.reject(insecure)
    }
    if (!hasGetUserMedia()) {
      var unsupported = new Error('microphone unavailable')
      unsupported.name = 'NotSupportedError'
      return Promise.reject(unsupported)
    }
    var mime = pickMimeType()
    engine = mime !== null ? 'mediarecorder' : 'wav'

    if (engine === 'wav') {
      var Ctx = getAudioContextClass()
      if (!Ctx) { return Promise.reject(new Error('recording not supported')) }
      // Create and resume the context synchronously inside the user gesture (iOS).
      audioCtx = new Ctx()
      if (audioCtx.state === 'suspended' && audioCtx.resume) {
        audioCtx.resume()
      }
      wavSampleRate = audioCtx.sampleRate
    }

    return requestStream().then(function (s) {
      if (disposed) {
        var tracks = s.getTracks ? s.getTracks() : []
        for (var i = 0; i < tracks.length; i++) { try { tracks[i].stop() } catch (e) {} }
        throw new Error('recorder disposed')
      }
      stream = s
      if (engine === 'mediarecorder') {
        setupMediaRecorder(mime)
      } else {
        setupWavNodes()
      }
      startedAt = now()
    })
  }

  function stop () {
    var duration = startedAt ? (now() - startedAt) / 1000 : 0
    if (engine === 'mediarecorder') {
      return stopMediaRecorder().then(function (blob) {
        stopTracks()
        return { blob: blob, mimeType: blob.type || finalBlobType(), duration: duration }
      })
    }
    var merged = mergeBuffers(wavBuffers, wavLength)
    var downsampled = downsample(merged, wavSampleRate, WAV_SAMPLE_RATE)
    var wavBlob = encodeWavBlob(downsampled, WAV_SAMPLE_RATE)
    teardownWavNodes()
    closeAudioCtx()
    stopTracks()
    return Promise.resolve({ blob: wavBlob, mimeType: 'audio/wav', duration: duration })
  }

  function dispose () {
    disposed = true
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      try { mediaRecorder.stop() } catch (e) {}
    }
    mediaRecorder = null
    teardownWavNodes()
    closeAudioCtx()
    stopTracks()
    wavBuffers = []
    chunks = []
  }

  return {
    start: start,
    stop: stop,
    dispose: dispose,
    engineType: function () { return engine }
  }
}



// WEBPACK FOOTER //
// ./src/utils/audioRecorder.js